import {
    IonBadge,
    IonButton, IonButtons,
    IonContent, IonHeader, IonIcon, IonInput,
    IonItem,
    IonItemDivider,
    IonLabel, IonList, IonModal, IonNote,
    IonRefresher, IonRefresherContent,
    IonSearchbar, IonSelect, IonSelectOption, IonTitle, IonToolbar,
    useIonRouter, useIonToast, useIonViewWillEnter
} from "@ionic/react";
import React, {useCallback, useEffect, useState} from "react";
import {add} from "ionicons/icons";
import {errorToastOptions} from "../../components/ErrorToast";
import PermissionRepositoryTypeIcon from "../../components/PermissionRepositoryTypeIcon";
import {
    createServiceType,
    getPermissionRepositoryTypes,
    getAdminServiceTypes
} from "../../repositories/adminServiceTypeRepository";

export const AdminServiceTypes = () => {
    const [serviceTypes, setServiceTypes] = useState([]);
    const [permissionRepositoryTypes, setPermissionRepositoryTypes] = useState([]);
    const [search, setSearch] = useState('');
    const [newServiceType, setNewServiceType] = useState(null);
    const [presentToast] = useIonToast();
    const router = useIonRouter();

    const presentError = useCallback(async (e, fallbackMessage) => {
        const message = typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallbackMessage;
        await presentToast(errorToastOptions(message));
    }, [presentToast]);

    const loadServiceTypes = useCallback(async () => {
        try {
            setServiceTypes(await getAdminServiceTypes());
        } catch (e) {
            await presentError(e, "Failed to load service types");
        }
    }, [presentError]);

    // reload when coming back from a service type, it may have been renamed or disabled
    useIonViewWillEnter(() => {
        loadServiceTypes();
    }, [loadServiceTypes]);

    useEffect(() => {
        getPermissionRepositoryTypes()
            .then(setPermissionRepositoryTypes)
            .catch(e => presentError(e, "Failed to load permission repository types"));
    }, [presentError]);

    const reload = async (event) => {
        try {
            await loadServiceTypes();
        } finally {
            event.detail.complete();
        }
    }

    const create = async () => {
        let serviceType;
        try {
            serviceType = await createServiceType(newServiceType.serviceTypeName, newServiceType.permissionRepositoryType);
        } catch (e) {
            await presentError(e, "Failed to create service type");
            return;
        }
        setNewServiceType(null);
        await presentToast({message: "Service type created, it stays disabled until you enable it", duration: 3000, color: "success"});
        router.push(`/admin/serviceTypes/${serviceType.serviceTypeId}`);
    }

    const normalizedSearch = search.trim().toLowerCase();
    const filteredServiceTypes = normalizedSearch
        ? serviceTypes.filter(serviceType =>
            [serviceType.serviceTypeName, serviceType.permissionRepositoryType, String(serviceType.serviceTypeId)]
                .some(value => value?.toLowerCase().includes(normalizedSearch)))
        : serviceTypes;

    return <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={reload}>
            <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonItemDivider sticky className="access-list-search-bar">
            <IonButton className="ion-margin-start" fill="outline" slot="start"
                       onClick={() => setNewServiceType({serviceTypeName: ''})}>
                <IonIcon icon={add} />
            </IonButton>
            <IonSearchbar
                placeholder="Search by name, repository type or id"
                debounce={300}
                mode="ios"
                enterkeyhint="search" inputmode="search" onIonInput={(ev) => setSearch(ev.detail.value ?? '')}></IonSearchbar>
        </IonItemDivider>

        <IonList>
            {filteredServiceTypes.length === 0 && (
                <IonItem lines="none">
                    <IonLabel color="medium">
                        {serviceTypes.length === 0 ? 'No service types' : 'No service types match the search'}
                    </IonLabel>
                </IonItem>
            )}
            {filteredServiceTypes.map(serviceType => (
                <IonItem button key={serviceType.serviceTypeId}
                         onClick={() => router.push(`/admin/serviceTypes/${serviceType.serviceTypeId}`)}>
                    <PermissionRepositoryTypeIcon slot="start" type={serviceType.permissionRepositoryType} />
                    <IonLabel>
                        <h3>{ serviceType.serviceTypeName }</h3>
                        <p>{ serviceType.permissionRepositoryType } · { serviceType.servicesCount } services · id { serviceType.serviceTypeId }</p>
                    </IonLabel>
                    {serviceType.isDisabled
                        ? <IonBadge slot="end" color="danger">Disabled</IonBadge>
                        : <IonBadge slot="end" color="success">Enabled</IonBadge>}
                </IonItem>
            ))}
        </IonList>

        <IonModal isOpen={newServiceType !== null} onDidDismiss={() => setNewServiceType(null)}>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton onClick={() => setNewServiceType(null)}>Cancel</IonButton>
                    </IonButtons>
                    <IonTitle>Create service type</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            disabled={!newServiceType?.serviceTypeName?.trim() || !newServiceType?.permissionRepositoryType}
                            strong={true}
                            onClick={() => create()}>
                            Create
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <IonItem>
                    <IonInput label="Service type name" labelPlacement="stacked" maxlength={64}
                              placeholder="Enter service type name"
                              value={newServiceType?.serviceTypeName}
                              onIonInput={(ev) => setNewServiceType(x => ({...x, serviceTypeName: ev.target.value}))}/>
                </IonItem>
                <IonItem>
                    <IonSelect label="Permission repository type" labelPlacement="stacked" placeholder="Select type"
                               value={newServiceType?.permissionRepositoryType}
                               onIonChange={(ev) => setNewServiceType(x => ({...x, permissionRepositoryType: ev.detail.value}))}>
                        {permissionRepositoryTypes.map(type =>
                            <IonSelectOption key={type} value={type}>{type}</IonSelectOption>)}
                    </IonSelect>
                </IonItem>
                <IonNote className="ion-padding-top" style={{display: 'block'}}>
                    The repository type can't be changed later. The service type is created disabled: set up its target
                    first (e.g. the parameters secret in the key vault), then enable it.
                </IonNote>
            </IonContent>
        </IonModal>
    </IonContent>
}
